<template>
  <div class="bg-purple-light">
    <div class="content">
      <navbar :is-logged-in="false" />

      <div class="d-flex justify-content-center">
        <div class="card my-5 shadow">
          <div class="card-body p-5">
            <h5 class="mb-2">
              <strong>{{ $t("personal-data.text-one") }}</strong>
            </h5>
            <h6 class="pb-4 border-bottom border-secondary">
              <small class="text-info font-weight-bold">
                {{ $t("personal-data.text-two") }}
              </small>
            </h6>
            <form class="pt-4">
              <div class="mb-3">
                <p class="text-label font-weight-bold mb-1">
                  {{ $t("personal-data.label.fullname") }}
                </p>
                <input
                  v-model.trim="$v.personalData.fullname.$model"
                  type="text"
                  class="form-control form-control-sm col-12 text-input"
                  :placeholder="$t('personal-data.placeholder.fullname')"
                  :class="{
                    'is-invalid': $v.personalData.fullname.$error
                  }"
                >
                <div class="text-danger font-weight-bold">
                  <small
                    v-if="
                      !$v.personalData.fullname.required &&
                        $v.personalData.fullname.$error === true
                    "
                  >{{ $t("personal-data.form-error.fullname-required") }}</small>
                </div>
              </div>
              <div class="mb-3">
                <p class="text-label font-weight-bold mb-1">
                  {{ $t("personal-data.label.phonenumber") }}
                </p>
                <input
                  v-model.trim="$v.personalData.phonenumber.$model"
                  type="text"
                  class="form-control form-control-sm col-12 text-input"
                  :placeholder="$t('personal-data.placeholder.phonenumber')"
                  :class="{
                    'is-invalid': $v.personalData.phonenumber.$error
                  }"
                >
                <div class="text-danger font-weight-bold">
                  <small
                    v-if="
                      !$v.personalData.phonenumber.required &&
                        $v.personalData.phonenumber.$error === true
                    "
                  >{{
                    $t("personal-data.form-error.phonenumber-required")
                  }}</small>
                  <small v-if="!$v.personalData.phonenumber.numeric">{{
                    $t("personal-data.form-error.phonenumber-numeric")
                  }}</small>
                  <small v-if="!$v.personalData.phonenumber.maxLength">{{
                    $t("personal-data.form-error.phonenumber-maxlength")
                  }}</small>
                </div>
              </div>
              <div
                class="mb-3 d-flex justify-content-between px-5 mx-3"
                :class="{ 'border border-danger': $v.personalData.gender.$error }"
              >
                <div>
                  <input
                    id="male"
                    v-model.trim="$v.personalData.gender.$model"
                    type="radio"
                    value="true"
                  >
                  <label
                    for="male"
                    class="label-radio pl-2"
                  >{{
                    $t("personal-data.label.male")
                  }}</label>
                </div>
                <div>
                  <input
                    id="female"
                    v-model.trim="$v.personalData.gender.$model"
                    type="radio"
                    value="false"
                  >
                  <label
                    for="female"
                    class="label-radio pl-2"
                  >{{
                    $t("personal-data.label.female")
                  }}</label>
                </div>
              </div>
              <div class="mb-3">
                <p class="text-label font-weight-bold mb-1">
                  {{ $t("personal-data.label.date") }}
                </p>
                <div
                  class="input-group"
                  :class="{'border border-danger': errorDateBirth || $v.dateBirth.$error}"
                >
                  <div class="pointer input-group-prepend">
                    <date-picker
                      v-model="dateBirth"
                      :model-config="modelConfig"
                      class="date-pick"
                      :max-date="today"
                    >
                      <template v-slot="{ togglePopover }">
                        <span
                          class="input-group-text bg-white text-info rounded-left"
                          @click="togglePopover({ placement: 'auto-start' })"
                        >
                          <i
                            class="fa fa-calendar"
                            aria-hidden="true"
                          />
                        </span>
                      </template>
                    </date-picker>
                  </div>
                  <input
                    v-model="dateBirth"
                    type="text"
                    class="form-control form-control-sm bg-white"
                    placeholder="dd/mm/yyyy"
                    disabled
                  >
                </div>
                <div class="text-danger font-weight-bold">
                  <small
                    v-if="!$v.dateBirth.required && $v.dateBirth.$error === true"
                  >{{ $t("personal-data.form-error.birthdate-required") }}</small>
                  <small
                    v-if="errorDateBirth === true"
                  >{{ $t("personal-data.form-error.birthdate-year") }}</small>
                </div>
              </div>
              <div class="pb-1">
                <p class="text-label font-weight-bold mb-1">
                  {{ $t("personal-data.label.address") }}
                </p>
                <textarea
                  v-model.trim="$v.personalData.address.$model"
                  class="form-control form-control-sm col-12 text-input"
                  :placeholder="$t('personal-data.placeholder.address')"
                  :class="{
                    'is-invalid': $v.personalData.address.$error
                  }"
                />
                <div class="text-danger font-weight-bold">
                  <small
                    v-if="
                      !$v.personalData.address.required &&
                        $v.personalData.address.$error === true
                    "
                  >{{ $t("personal-data.form-error.address-required") }}</small>
                </div>
              </div>
            </form>
            <div class="mt-4">
              <div class="d-flex justify-content-center">
                <button
                  type="button"
                  class="btn btn-outline-info mr-3"
                  @click.prevent="previousPage"
                >
                  <small class="font-weight-bold">
                    {{ $t("personal-data.button.previous") }}
                  </small>
                </button>
                <button
                  type="button"
                  class="btn btn-info ml-3"
                  @click="nextPage"
                >
                  <small class="font-weight-bold">
                    {{ $t("personal-data.button.next") }}
                  </small>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar'
import DatePicker from 'v-calendar/lib/components/date-picker.umd'

import {
  required,
  minLength,
  maxLength,
  numeric,
  helpers
} from 'vuelidate/lib/validators'

export default {
  components: {
    Navbar,
    DatePicker
  },

  data () {
    return {
      dateBirth: '',
      errorDateBirth: false,
      modelConfig: {
        type: 'string',
        mask: 'YYYY-MM-DD'
      },
      personalData: {
        fullname: '',
        phonenumber: '',
        gender: '',
        address: ''
      },
      dataStorage: null,
      today: new Date()
    }
  },

  watch: {
    dateBirth () {
      const splitDate = this.dateBirth.split('-')
      const date = new Date()
      const year = date.getFullYear().toString()
      this.errorDateBirth = splitDate[0] === year
    }
  },

  mounted () {
    if (localStorage.getItem('HRISAccount')) {
      this.dataStorage = JSON.parse(localStorage.getItem('HRISAccount'))
      this.personalData.fullname = this.dataStorage.fullname
    }
    if (localStorage.getItem('PersonalData')) {
      const locPersonalData = JSON.parse(localStorage.getItem('PersonalData'))
      this.personalData = locPersonalData
      this.dateBirth = locPersonalData.dateBirth
    }
  },

  methods: {
    previousPage () {
      this.$router.go(-1)
    },
    nextPage () {
      this.$v.$touch()
      if (!this.$v.$invalid && !this.errorDateBirth) {
        this.personalData.dateBirth = this.dateBirth
        localStorage.PersonalData = JSON.stringify(this.personalData)
        this.$router.push({
          name: 'companydetail'
        })
        window.scrollTo(0, 0)
      }
    }
  },

  validations: {
    personalData: {
      fullname: {
        required,
        minLength: minLength(5),
        maxLength: maxLength(255)
      },
      phonenumber: {
        required,
        maxLength: maxLength(25),
        numeric: helpers.regex(numeric, /^[+0-9]*$/i)
      },
      address: {
        required
      },
      gender: {
        required
      }
    },
    dateBirth: {
      required
    }
  }
}
</script>

<style scoped>
.content {
  background-image: url("~@/assets/image/bg-2.png");
  background-position: bottom;
  background-repeat: no-repeat;
  background-size: contain;
  min-height: 100vh;
}
.card {
  border-radius: 12px;
  margin: 0 auto;
  width: 390px;
}
.border-secondary {
  border-bottom: 2px solid #DEDEDE !important;
}
.text-label {
  font-size: 12px;
  line-height: 14px;
}
.label-radio {
  font-size: 13px;
  font-weight: 600;
  vertical-align: middle;
  margin-bottom: 2px;
}
.pointer {
  cursor: pointer;
}
textarea {
  resize: none;
}
textarea::-webkit-input-placeholder,
input::-webkit-input-placeholder {
  font-weight: 400;
  font-size: 12px;
  color: #aaaaaa;
}
button {
  width: 200px;
}
</style>
